import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { KpiCard } from "@/components/KpiCard";
import { FileText, CheckCircle2, Clock, Archive, CalendarClock, Languages, Gauge, MapPin } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function Dashboard() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((res) => setData(res.data));
  }, []);

  if (!data) {
    return (
      <div className="p-6 text-slate-400 font-mono text-sm" data-testid="dashboard-loading">
        {t("common.loading")}
      </div>
    );
  }

  const kpis = [
    { testId: "kpi-total", label: lang === "fr" ? "Total Protocoles" : "Total Protocols", value: data.total, icon: FileText, status: "neutral", to: "/protocoles" },
    { testId: "kpi-valides", label: t("statuses.VALIDE"), value: data.valides, icon: CheckCircle2, status: "success", to: "/protocoles?statut=VALIDE" },
    { testId: "kpi-a-valider", label: t("nav.to_validate"), value: data.a_valider, icon: Clock, status: "warning", to: "/a-valider" },
    { testId: "kpi-obsoletes", label: t("statuses.OBSOLETE"), value: data.obsoletes, icon: Archive, status: "danger", to: "/protocoles?statut=OBSOLETE" },
    { testId: "kpi-echeance", label: lang === "fr" ? "Échéance < 30j" : "Due < 30 days", value: data.echeance, icon: CalendarClock, status: "warning", to: "/protocoles?echeance=30" },
    { testId: "kpi-traductions", label: lang === "fr" ? "Traductions à actualiser" : "Translations to refresh", value: data.traductions_a_actualiser, icon: Languages, status: "info", to: "/traductions?obsolete=1" },
    { testId: "kpi-score", label: t("common.compliance_score"), value: `${data.score_conformite}%`, icon: Gauge, status: "success", to: "/referentiel?tab=regles" },
    { testId: "kpi-sites", label: lang === "fr" ? "Sites Couverts" : "Sites Covered", value: data.sites_count, icon: MapPin, status: "neutral", to: "/administration" },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="dashboard-view">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{t("nav.dashboard")}</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <KpiCard key={k.testId} {...k} />
        ))}
      </div>
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5" data-testid="site-breakdown-chart">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">{lang === "fr" ? "Répartition par Site" : "Breakdown by Site"}</h2>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.repartition_sites}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="code" stroke="#64748b" fontSize={12} />
            <YAxis stroke="#64748b" fontSize={12} />
            <Tooltip />
            <Legend />
            <Bar dataKey="valides" name={t("statuses.VALIDE")} fill="#16A34A" radius={[4, 4, 0, 0]} />
            <Bar dataKey="a_valider" name={t("nav.to_validate")} fill="#D97706" radius={[4, 4, 0, 0]} />
            <Bar dataKey="obsoletes" name={t("statuses.OBSOLETE")} fill="#DC2626" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
